import {
  ALIGN_CENTER,
  ALIGN_RIGHT,
  BG_CELL_TITLE,
  BORDER_CELL,
  FONT_TITLE,
  STYLE_TITLE_TABLE,
  TOTALS_STYLE,
} from '../data/excelJsData';
import { getAlphabet } from '../utils/excelJsUtil';

/**
 * Decorador para crear tablas en las hojas (worksheets) de ExcelJs
 *
 * En esta clase solo se debe acceder a las funciones de AddTable y addTotalColumn.
 *
 * Las demás funciones son privadas solo para clase
 */
class TableExcelJsDecorator {
  constructor(worksheet, props) {
    this.ws = worksheet;
    this.TITLE_ROW_HEIGHT = props?.titleRowHeigth ?? 20;
    this.BG_CELL_TITLE = props?.bgCellTitle ?? this.BG_CELL_TITLE;
  }

  /**
   * Permite agregar una tabla a la hoja de exceljs
   * @param {int} startRow Fila donde inicia la tabla
   * @param {array} columns Listado de columnas de la tabla
   * @param {array} data Listado de datos o registros de la tabla
   * @returns {object} Devuelve el this para poder enlazar la función con otras
   */
  addTable(startRow, columns, data, props = {}) {
    const {
      showTotal,
      keyColumnShowTotal,
      createEmptyRow = true,
      totalLabel = 'TOTAL',
    } = props;
    this._setDataTable(startRow, columns, data, createEmptyRow);
    this._addHeaders();
    this._insertRows();
    if (showTotal) {
      this._addTotalColumns(keyColumnShowTotal, totalLabel);
    }
    return this;
  }

  /**
   * Agrega las columnas de total a la tabla
   * @param {string} keyColumnShowTotal Columna donde se mostrará el texto del total
   * @param {string} totalLabel Texto a mostrar en la columna de total
   */
  _addTotalColumns(keyColumnShowTotal, totalLabel = 'TOTAL') {
    const START_ROW = this.startRow + this.data.length + 1;

    this.columns.forEach((column, index) => {
      const { style, key, showTotal, t } = column;
      const cell = `${this.ALPHABET[index]}${START_ROW}`;

      if (showTotal) {
        const total = this._getTotalColumn(key);
        const cellRef = this.ws.getCell(cell);
        cellRef.value = total;
        this.aplicarFormatoCelda(cellRef, t);
      } else {
        this.ws.getCell(cell).value = '';
      }

      this.ws.getCell(cell).font = this.FONT_TITLE;
      this.ws.getCell(cell).fill = this.BG_CELL_TITLE;
      this.ws.getCell(cell).alignment = style.alignment;
      this.ws.getCell(cell).border = this.BORDER_CELL;

      if (key === keyColumnShowTotal) {
        this.ws.getCell(cell).value = totalLabel;
        this.ws.getCell(cell).alignment = {
          ...style.alignment,
          horizontal: 'center',
        };
      }
    });
    this.ws.getRow(START_ROW).height = this.TITLE_ROW_HEIGHT;

    return this;
  }

  _getTotalColumn(keyColumn) {
    return this.data.reduce((acc, column) => {
      return acc + Number.parseFloat(column[keyColumn]);
    }, 0);
  }

  /**
   * Carga a la clase, las propiedades necesarias para crear una tabla
   */
  _setDataTable(startRow, columns, data, createEmptyRow = true) {
    this.ALPHABET = getAlphabet();
    this.startRow = startRow;
    this.columns = columns;
    this.data = data;
    this.createEmptyRow = createEmptyRow;
  }

  /**
   * Crea los headers o títulos de la tabla
   */
  _addHeaders() {
    this.columns.forEach(({ name: nombreColumna }, index) => {
      const cell = `${this.ALPHABET[index]}${this.startRow}`;
      this.ws.getCell(cell).value = nombreColumna;
      this.ws.getCell(cell).style = this.STYLE_TITLE_TABLE;
    });
    this.ws.getRow(this.startRow).height = this.TITLE_ROW_HEIGHT;
  }

  /**
   * Inserta los registros a la tabla
   */
  _insertRows() {
    // 1. Crea las columnas en la hoja
    this.ws.columns = this.columns;
    const copyData = [...this.data];
    const primerRegistro = copyData.shift();

    if (primerRegistro === undefined && !this.createEmptyRow) return;

    // 2. Crea el primer registro
    this.ws.insertRow(this.startRow + 1, primerRegistro);

    this.columns.forEach((column, index) => {
      const cellRef = this.ws.getCell(
        `${this.ALPHABET[index]}${this.startRow + 1}`
      );
      this.aplicarFormatoCelda(cellRef, column.t);
      cellRef.border = this.BORDER_CELL;
      cellRef.alignment = column.alignment ?? column.style?.alignment;
    });

    // 3. Crea las demás cuotas, heredando los estilos de la primera
    this.ws.insertRows(this.startRow + 2, copyData, 'i'); // i: Heredar estilos
  }

  /**
   * Aplica formato numérico a una celda según el tipo
   */
  aplicarFormatoCelda(celda, tipoFormato) {
    const formatos = {
      money: '$#,##0.00',
      number: '0',
      numeroConMiles: '#,##0',
    };

    if (formatos[tipoFormato]) {
      celda.numFmt = formatos[tipoFormato];
    }
  }
}

/**
 * Agrega propiedades de estilos a la clase, con el objetivo de reutilizar código.
 * Estas pripiedades se encuentran definidas en el diccionario de datos importado.
 *
 * No agregar estilos que no se usen dentro de la clase.
 */
TableExcelJsDecorator.prototype.ALIGN_CENTER = ALIGN_CENTER;
TableExcelJsDecorator.prototype.ALIGN_RIGHT = ALIGN_RIGHT;
TableExcelJsDecorator.prototype.BORDER_CELL = BORDER_CELL;
TableExcelJsDecorator.prototype.STYLE_TITLE_TABLE = STYLE_TITLE_TABLE;
TableExcelJsDecorator.prototype.BG_CELL_TITLE = BG_CELL_TITLE;
TableExcelJsDecorator.prototype.TOTALS_STYLE = TOTALS_STYLE;
TableExcelJsDecorator.prototype.FONT_TITLE = FONT_TITLE;

export default TableExcelJsDecorator;
